import { useCallback, useState } from 'react';
import styles from './ConversionOptions.module.css';
import { useConversionStore, type ConversionOptions as Options } from '@stores/conversionStore';

export interface ConversionOptionsProps {
  disabled?: boolean;
  defaultCollapsed?: boolean;
}

export function ConversionOptions({
  disabled = false,
  defaultCollapsed = true,
}: ConversionOptionsProps) {
  const options = useConversionStore((s) => s.options);
  const setOptions = useConversionStore((s) => s.setOptions);
  const [open, setOpen] = useState(!defaultCollapsed);

  const handleChange = useCallback(
    (key: keyof Options, value: string | number | boolean | undefined) => {
      setOptions({ [key]: value === '' ? undefined : value });
    },
    [setOptions]
  );

  return (
    <div className={styles.container}>
      <button
        className={styles.toggle}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14" />
        </svg>
        Advanced Options
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="13"
          height="13"
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={styles.panel}>
          <p className={styles.sectionTitle}>Profile Identity</p>
          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.label}>Profile Name</span>
              <input
                type="text"
                value={options.name ?? ''}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Cinematic Film"
                disabled={disabled}
              />
              <span className={styles.hint}>Main display name in Lightroom</span>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Short Name</span>
              <input
                type="text"
                value={options.shortName ?? ''}
                onChange={(e) => handleChange('shortName', e.target.value)}
                placeholder="e.g., CineFilm"
                disabled={disabled}
              />
            </label>
          </div>
          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.label}>Sort Name</span>
              <input
                type="text"
                value={options.sortName ?? ''}
                onChange={(e) => handleChange('sortName', e.target.value)}
                placeholder="Leave blank to use Profile Name"
                disabled={disabled}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Group</span>
              <input
                type="text"
                value={options.group ?? ''}
                onChange={(e) => handleChange('group', e.target.value)}
                placeholder="e.g., Film Emulation"
                disabled={disabled}
              />
              <span className={styles.hint}>Category in profile browser</span>
            </label>
          </div>
          <label className={styles.field}>
            <span className={styles.label}>Description</span>
            <textarea
              value={options.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what this profile does..."
              disabled={disabled}
            />
          </label>

          <p className={styles.sectionTitle}>LUT Settings</p>
          <label className={styles.field} style={{ maxWidth: '180px' }}>
            <span className={styles.label}>LUT Size</span>
            <input
              type="number"
              min={2}
              max={64}
              value={options.lutSize ?? 32}
              onChange={(e) => handleChange('lutSize', Number(e.target.value))}
              disabled={disabled}
              style={{ width: '100px' }}
            />
            <span className={styles.hint}>Target resolution (2–64). Default: min(source, 32)</span>
          </label>
          {options.lutSize && options.lutSize > 32 && (
            <p className={styles.warn}>⚠️ LUT sizes larger than 32 may cause compatibility issues with older Lightroom versions.</p>
          )}

          <p className={styles.sectionTitle}>Visibility</p>
          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={options.showInPresets}
                onChange={(e) => handleChange('showInPresets', e.target.checked)}
                disabled={disabled}
              />
              Show in Presets
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={options.showInQuickActions}
                onChange={(e) => handleChange('showInQuickActions', e.target.checked)}
                disabled={disabled}
              />
              Show in Quick Actions
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={options.requiresRgbTables}
                onChange={(e) => handleChange('requiresRgbTables', e.target.checked)}
                disabled={disabled}
              />
              Requires RGB Tables
            </label>
          </div>

          <p className={styles.sectionTitle}>Advanced</p>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={options.allowLarge}
              onChange={(e) => handleChange('allowLarge', e.target.checked)}
              disabled={disabled}
            />
            Allow large LUT sizes (&gt;32) without warning
          </label>
        </div>
      )}
    </div>
  );
}

export default ConversionOptions;
